import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import Sidebar from "@/components/Sidebar";
import MenuSheet from "@/components/MenuSheet";
import { CalendarDays } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import CalendarMonth from "@/components/CalendarMonth";
import DayPanel from "@/components/DayPanel";
import DayDetailsDialog from "@/components/DayDetailsDialog";
import GoalsSpotlight from "@/components/GoalsSpotlight";
import ChecklistBanner from "@/components/ChecklistBanner";
import UrgentTasksBanner from "@/components/UrgentTasksBanner";
import SaturdayCelebration from "@/components/SaturdayCelebration";
import QuoteOfTheDay from "@/components/QuoteOfTheDay";
import TodaysFocus from "@/components/TodaysFocus";
import DailyReminderBanner from "@/components/DailyReminderBanner";
import SundayReminder from "@/components/SundayReminder";
import { scheduleEventNotifications } from "@/lib/notifications";

export default function PlannerPage() {
  const { user } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [todayTasks, setTodayTasks] = useState([]);
  const [dayOpen, setDayOpen] = useState(false);

  const loadEvents = useCallback(async () => {
    const year = currentDate.getFullYear();
    const start = `${year}-01-01`;
    const end = `${year}-12-31`;
    const { data } = await api.get("/events", { params: { start, end } });
    setEvents(data);
    scheduleEventNotifications(data);
  }, [currentDate]);

  const loadTodayTasks = useCallback(async () => {
    const t = new Date();
    const key = `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
    try {
      const { data } = await api.get("/tasks", { params: { date: key } });
      setTodayTasks(data || []);
    } catch {
      setTodayTasks([]);
    }
  }, []);

  useEffect(() => {
    loadEvents();
    loadTodayTasks();
  }, [loadEvents, loadTodayTasks]);

  return (
    <div className="min-h-screen">
      <SaturdayCelebration />
      <div className="max-w-[1500px] mx-auto p-4 sm:p-6 lg:p-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <Sidebar currentDate={currentDate} />
          <main className="lg:col-span-7 order-1 lg:order-2 space-y-6" data-testid="main-content">
            {/* Mobile-only header — Menu + Lumivera wordmark. On desktop the
                same content lives inside the Sidebar which sits on the left. */}
            <div className="flex lg:hidden items-center gap-2" data-testid="mobile-header">
              <MenuSheet />
              <div className="flex items-center gap-2 text-primary flex-1">
                <CalendarDays strokeWidth={1.5} className="size-7" />
                <span className="font-display font-extrabold text-2xl tracking-tight text-foreground">
                  Lumivera
                </span>
              </div>
              <span
                className="text-xs text-muted-foreground truncate max-w-[110px]"
                title={user?.email}
              >
                {user?.name}
              </span>
            </div>
            <SundayReminder />
            {/* 1. Top priority tasks (red urgent banner) */}
            <UrgentTasksBanner />
            {/* 2. Today's focus — user-editable */}
            <TodaysFocus />
            {/* 3. Daily Reminder nudge */}
            <DailyReminderBanner />
            {/* 4. Calendar */}
            <CalendarMonth
              currentDate={currentDate}
              setCurrentDate={setCurrentDate}
              selectedDate={selectedDate}
              setSelectedDate={(d) => {
                setSelectedDate(d);
                setDayOpen(true);
              }}
              events={events}
              todayTasks={todayTasks}
            />
            {/* 5. Quote of the day */}
            <QuoteOfTheDay />
            {/* 6. Today's checklist */}
            <ChecklistBanner />
            {/* 7. Selected day details */}
            <DayPanel
              selectedDate={selectedDate}
              events={events}
              onEventsChanged={loadEvents}
            />
            {/* 8. Goals */}
            <GoalsSpotlight />
          </main>
        </div>
      </div>
      <DayDetailsDialog
        open={dayOpen}
        onOpenChange={setDayOpen}
        selectedDate={selectedDate}
        events={events}
        onEventsChanged={loadEvents}
      />
    </div>
  );
}
